;(function (angular, _) {
  'use strict'

  angular
    .module('kilo.routes.pennylane.inbox')
    .controller('PennylaneInboxListController', PennylaneInboxListController)

  PennylaneInboxListController.$inject = [
    // Angular
    '$scope',
    // Libs
    // Managers
    // Services
    'PennylaneService',
    'DownloaderService',
    'SnackService',
    // Const
    // Resolved
    'pagination'
  ]

  /**
   * Liste des factures d'achat reçues via Pennylane (direction `supplier`).
   *
   * Le backend (`GET /invoices?direction=supplier`) renvoie un TABLEAU BRUT
   * (pas d'enveloppe paginée) trié `createdAt` décroissant → la pagination est
   * faite côté client (tranche de `allInvoices`), branchée sur la directive
   * `<pagination>` existante via `refreshPage()` (aucun re-fetch au changement
   * de page). Le tri d'affichage est par date de RÉCEPTION (`createdAt`)
   * décroissante, tie-breaks `date` puis `invoiceNumber` (voir sortByDateDesc —
   * duplique volontairement le sort backend, défensif).
   */
  function PennylaneInboxListController (
    // Angular
    $scope,
    // Libs
    // Managers
    // Services
    PennylaneService,
    DownloaderService,
    SnackService,
    // Const
    // Resolved
    pagination
  ) {
    const self = this

    // Controller détruit (navigation pendant un appel en vol) : les callbacks
    // de refreshInvoice/getList ne remplacent plus rien dans la liste et ne
    // snackent plus (les flags loading/verrous restent levés, sans effet).
    let destroyed = false

    // Libellés FR des statuts e-invoicing (enum backend EInvoicingStatus —
    // union émission/réception). Statut inconnu → valeur brute affichée.
    const E_INVOICING_LABELS = {
      waiting_for_validation: 'En attente de validation',
      submitted: 'Soumise',
      sent: 'Envoyée',
      approved: 'Approuvée',
      accepted: 'Acceptée',
      collected: 'Encaissée',
      partially_collected: 'Partiellement encaissée',
      disputed: 'En litige',
      in_dispute: 'En litige',
      refused: 'Refusée',
      rejected: 'Rejetée'
    }

    self.$onInit = () => {
      _.extend(self, {
        // Public Attributes
        pagination: pagination,
        /** Liste complète renvoyée par le backend (source de la pagination client). */
        allInvoices: [],
        /** Tranche affichée (page courante). */
        invoices: [],
        loading: false,
        loadError: false,
        /**
         * Verrous d'actualisation par ligne, indexés par `_id` (map du
         * controller, PAS un flag sur l'objet facture : l'objet est REMPLACÉ
         * au succès et le payload backend reste propre).
         */
        refreshing: {},
        // Public Methods
        getList,
        refreshPage,
        download,
        refreshInvoice,
        eInvoicingLabel
      })

      $scope.$on('$destroy', () => {
        destroyed = true
      })

      getList()
    }

    /** Recharge la liste complète depuis le backend puis raffiche la page courante. */
    function getList () {
      self.loading = true
      self.loadError = false
      PennylaneService.getInvoices({ direction: 'supplier' })
        .then((res) => {
          if (destroyed) {
            return
          }
          self.allInvoices = sortByDateDesc((res && res.data) || [])
          self.pagination.totalElements = self.allInvoices.length
          clampCurrentPage()
          refreshPage()
        })
        .catch(() => {
          // Le toast global (HttpErrorInterceptor) porte déjà le détail de
          // l'erreur — on ne garde ici que l'état local pour le « Réessayer ».
          self.loadError = true
        })
        .finally(() => {
          self.loading = false
        })
    }

    /**
     * Raffiche la tranche correspondant à la page courante (pagination côté
     * client) — callback `get-list` de la directive `<pagination>` : PAS de
     * re-fetch au changement de page / de taille de page.
     */
    function refreshPage () {
      clampCurrentPage()
      const start = self.pagination.perPage * self.pagination.currentPage
      self.invoices = self.allInvoices.slice(start, start + self.pagination.perPage)
    }

    /** Télécharge le PDF/Factur-X via le downloader 90tech (jamais d'URL présignée Pennylane). */
    function download (invoice) {
      if (!invoice || !invoice.fileId) {
        return
      }
      DownloaderService.downloadFile({ _id: invoice.fileId })
    }

    /**
     * Resynchronise UNE facture reçue depuis Pennylane
     * (`POST /invoices/:id/reception/refresh`) puis remplace la ligne dans la
     * liste par la version à jour (même `_id` → `track by` intact ; `_id`
     * différent — recréation post-soft-delete côté backend — → rechargement
     * complet). Verrou par ligne via `self.refreshing[_id]` : l'icône devient
     * spinner, clic ignoré pendant l'appel. Les erreurs attendues (404/409…)
     * sont restituées en snack — jamais de toast global (ignoreErrors côté
     * service).
     */
    function refreshInvoice (invoice) {
      if (!invoice || !invoice._id || self.refreshing[invoice._id]) {
        return
      }
      // `_id` capturé en string : le finally reste correct même si l'objet
      // facture est remplacé ou la liste rechargée entre-temps.
      const invoiceId = invoice._id
      self.refreshing[invoiceId] = true
      PennylaneService.refreshReceptionInvoice(invoiceId)
        .then((res) => {
          if (destroyed) {
            return
          }
          const updated = res && res.data
          if (updated && updated._id) {
            const index = _.findIndex(self.allInvoices, { _id: updated._id })
            if (index !== -1) {
              self.allInvoices[index] = updated
              // La date de réception peut avoir bougé → re-tri + re-tranche.
              self.allInvoices = sortByDateDesc(self.allInvoices)
              refreshPage()
            } else {
              // Ligne introuvable (`_id` différent ou liste rechargée entre-
              // temps) → rechargement complet, seule façon d'afficher l'état
              // à jour.
              getList()
            }
          }
          SnackService.snack({ text: 'Facture actualisée.', delay: 5000 })
        })
        .catch((err) => {
          if (destroyed) {
            return
          }
          let text = 'Échec de l\'actualisation — réessayez.'
          if (err && err.status === 409) {
            // Message FR du backend (non synchronisée / rafraîchissement déjà
            // en cours) si présent ET string (les exceptions Nest typées
            // peuvent double-emballer un objet dans `message`), sinon
            // générique.
            const msg = err.data && err.data.message
            text = (typeof msg === 'string' && msg) || 'Actualisation impossible.'
          } else if (err && err.status === 404) {
            text = 'Facture introuvable ou entreprise non connectée à Pennylane.'
          }
          SnackService.snack({ text: text, delay: 5000 })
        })
        .finally(() => {
          delete self.refreshing[invoiceId]
        })
    }

    /** Libellé FR d'un statut e-invoicing ; statut inconnu → brut ; absent → null. */
    function eInvoicingLabel (status) {
      if (!status) {
        return null
      }
      return E_INVOICING_LABELS[status] || status
    }

    // ── Privé ────────────────────────────────────────────────────────────────

    /**
     * Tri par date de réception (`createdAt`) décroissante, puis date de
     * facture (`date`) décroissante, puis `invoiceNumber` décroissant
     * (comparaison string) — tie-breaks indispensables : après un import
     * initial complet, tous les `createdAt` sont quasi identiques.
     * Duplique VOLONTAIREMENT le sort du backend (défensif : l'affichage ne
     * dépend pas de l'ordre renvoyé, notamment après un remplacement de ligne).
     */
    function sortByDateDesc (list) {
      return list.slice().sort((a, b) =>
        (sortTimestamp(b, 'createdAt') - sortTimestamp(a, 'createdAt')) ||
        (sortTimestamp(b, 'date') - sortTimestamp(a, 'date')) ||
        String(b.invoiceNumber || '').localeCompare(String(a.invoiceNumber || ''))
      )
    }

    /** Timestamp du champ date demandé (absent/invalide → 0, relégué en fin de tri desc). */
    function sortTimestamp (invoice, field) {
      const raw = invoice[field]
      const time = raw ? new Date(raw).getTime() : NaN
      return isNaN(time) ? 0 : time
    }

    /** Ramène la page courante dans les bornes après un rechargement (liste raccourcie). */
    function clampCurrentPage () {
      const lastPage = Math.max(0, Math.ceil(self.pagination.totalElements / self.pagination.perPage) - 1)
      if (self.pagination.currentPage > lastPage) {
        self.pagination.currentPage = lastPage
      }
    }
  }
})(window.angular, window._)
